// Admins & RBAC — wraps the list / roles sub-routes with a tabbed
// header. The detail view (`/admins/$id`) sits inside the same parent
// route for file-based-routing reasons but renders without the tabbed
// chrome — see the `isDetailRoute` check below — so the detail
// dashboard reads as a focused profile, not a section header.
import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { cn } from "@/lib/cn";

const tabs = [
  { to: "/admins", label: "Admins", exact: true },
  { to: "/admins/roles", label: "Roles & permissions" },
] as const;

export const Route = createFileRoute("/_app/admins")({
  component: AdminsLayout,
});

function AdminsLayout() {
  const { location } = useRouterState();
  // Detail routes (/admins/<id>) skip the section header — the detail
  // page renders its own back-link + profile chrome and the tabs would
  // just compete for attention.
  const isDetailRoute =
    location.pathname !== "/admins" &&
    location.pathname !== "/admins/roles" &&
    location.pathname.startsWith("/admins/");
  if (isDetailRoute) return <Outlet />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Admins & RBAC</h1>
        <p className="text-sm text-muted">
          Operator accounts with role-based permission grants. Built-in roles: SUPER_ADMIN, MARKET_OPS, FINANCE, SUPPORT, READONLY.
        </p>
      </div>
      <div className="flex gap-1 border-b border-border">
        {tabs.map((t) => {
          const active =
            "exact" in t && t.exact
              ? location.pathname === t.to
              : location.pathname.startsWith(t.to);
          return (
            <Link key={t.to} to={t.to} className={cn(
              "border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              active ? "border-accent text-fg" : "border-transparent text-muted hover:text-fg",
            )}>{t.label}</Link>
          );
        })}
      </div>
      <Outlet />
    </div>
  );
}
